/**
 * Staff Attendance Service - API client for marking and viewing staff
 * attendance. This is what feeds payroll's attendance-based deduction rules.
 */
import api from '../utils/api';

const staffAttendanceService = {
  getRoster: async (attendanceDate) => {
    try {
      return await api.get('/api/attendance/staff-roster', { params: { attendance_date: attendanceDate } });
    } catch (error) {
      console.error('Error fetching staff attendance roster:', error);
      throw error;
    }
  },

  markOne: async (data) => {
    try {
      return await api.post('/api/attendance/staff', data);
    } catch (error) {
      console.error('Error recording staff attendance:', error);
      throw error;
    }
  },

  markBulk: async (attendanceDate, records) => {
    try {
      return await api.post('/api/attendance/staff/bulk', { attendance_date: attendanceDate, records });
    } catch (error) {
      console.error('Error recording bulk staff attendance:', error);
      throw error;
    }
  },

  getMyAttendance: async (startDate = null, endDate = null) => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      return await api.get('/api/attendance/staff/my', { params });
    } catch (error) {
      console.error('Error fetching my attendance:', error);
      throw error;
    }
  },

  getStaffAttendance: async (staffId, startDate = null, endDate = null) => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      return await api.get(`/api/attendance/staff/${staffId}`, { params });
    } catch (error) {
      console.error('Error fetching staff attendance:', error);
      throw error;
    }
  },

  correctRecord: async (attendanceId, status, remarks = '') => {
    try {
      return await api.put(`/api/attendance/staff-records/${attendanceId}`, { status, remarks });
    } catch (error) {
      console.error('Error correcting staff attendance record:', error);
      throw error;
    }
  },

  // ---- Self-service clock-in/out ----

  getTodayStatus: async () => {
    try {
      return await api.get('/api/attendance/staff/today');
    } catch (error) {
      console.error('Error fetching today\'s attendance status:', error);
      throw error;
    }
  },

  clockIn: async (qrToken = null) => {
    try {
      return await api.post('/api/attendance/staff/clock-in', { qr_token: qrToken });
    } catch (error) {
      console.error('Error clocking in:', error);
      throw error;
    }
  },

  clockOut: async (qrToken = null) => {
    try {
      return await api.post('/api/attendance/staff/clock-out', { qr_token: qrToken });
    } catch (error) {
      console.error('Error clocking out:', error);
      throw error;
    }
  },

  // ---- Clock-in mode settings (admin/HR) ----

  getSettings: async () => {
    try {
      return await api.get('/api/attendance/staff/settings');
    } catch (error) {
      console.error('Error fetching staff attendance settings:', error);
      throw error;
    }
  },

  updateSettings: async (data) => {
    try {
      return await api.put('/api/attendance/staff/settings', data);
    } catch (error) {
      console.error('Error updating staff attendance settings:', error);
      throw error;
    }
  },

  rotateQrToken: async () => {
    try {
      return await api.post('/api/attendance/staff/settings/qr-token/rotate', {});
    } catch (error) {
      console.error('Error rotating QR token:', error);
      throw error;
    }
  },

  // ---- Shifts (admin/HR) ----

  listShifts: async (includeInactive = false) => {
    try {
      return await api.get('/api/attendance/shifts', { params: { include_inactive: includeInactive } });
    } catch (error) {
      console.error('Error fetching shifts:', error);
      throw error;
    }
  },

  createShift: async (data) => {
    try {
      return await api.post('/api/attendance/shifts', data);
    } catch (error) {
      console.error('Error creating shift:', error);
      throw error;
    }
  },

  updateShift: async (shiftId, data) => {
    try {
      return await api.put(`/api/attendance/shifts/${shiftId}`, data);
    } catch (error) {
      console.error('Error updating shift:', error);
      throw error;
    }
  },

  deleteShift: async (shiftId) => {
    try {
      return await api.delete(`/api/attendance/shifts/${shiftId}`);
    } catch (error) {
      console.error('Error deleting shift:', error);
      throw error;
    }
  },

  setDefaultShift: async (shiftId) => {
    try {
      return await api.post(`/api/attendance/shifts/${shiftId}/set-default`, {});
    } catch (error) {
      console.error('Error setting default shift:', error);
      throw error;
    }
  },

  assignStaffShift: async (staffId, shiftId) => {
    try {
      return await api.put(`/api/attendance/staff/${staffId}/shift`, { shift_id: shiftId });
    } catch (error) {
      console.error('Error assigning staff shift:', error);
      throw error;
    }
  },

  // Records the PIN this staff member was assigned at a fingerprint/face
  // terminal's own local enrollment step — see routers/biometric_adms.py.
  setBiometricPin: async (staffId, pin) => {
    try {
      return await api.put(`/api/attendance/staff/${staffId}/biometric-pin`, { pin });
    } catch (error) {
      console.error('Error setting staff biometric PIN:', error);
      throw error;
    }
  },

  bulkAssignShift: async (staffIds, shiftId) => {
    try {
      return await api.post('/api/attendance/staff/shift/bulk-assign', { staff_ids: staffIds, shift_id: shiftId });
    } catch (error) {
      console.error('Error bulk-assigning shift:', error);
      throw error;
    }
  },

  triggerAutoAbsent: async (attendanceDate = null) => {
    try {
      return await api.post('/api/attendance/staff/auto-absent', { attendance_date: attendanceDate });
    } catch (error) {
      console.error('Error triggering auto-absent:', error);
      throw error;
    }
  },

  // ---- Export ----

  exportCsv: async (startDate, endDate) => {
    try {
      return await api.get('/api/attendance/staff/export', { params: { start_date: startDate, end_date: endDate }, raw: true });
    } catch (error) {
      console.error('Error exporting staff attendance:', error);
      throw error;
    }
  },
};

export default staffAttendanceService;
